import React, { useState } from "react";
import { useDateTimeDisplay } from '../../../../../platform/util/useDateTimeDisplay';
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { getBookingShareStatus } from './bookingShareStatus';
import BookingShareNotice from './bookingShareNotice';
import { scheduleDateLabel } from '../../../../../platform/util/bookingCalendarLabel';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Divider,
  Box,
  Link,
  Tooltip,
  IconButton,
  ClickAwayListener,
} from "@mui/material";
import { Recurring } from "../../../../../platform/types";



const BookingSummary = ({
  bookingType,
  startDate,
  endDate,
  totalDays,
  weeklyAvailability,
  availabilityHost,
  availabilityTimezone,
  hasAvailabilitySchedule,
  shareAvailabilityOptions,
  onEditDuration,
  onEditDateRange,
  onEditWeeklyAvailability,
}) => {
  const [helpOpen, setHelpOpen] = useState(false);
  const { displayCalendar, clockLabel } = useDateTimeDisplay();
  const startLabel = scheduleDateLabel(bookingType?.start || startDate, bookingType, availabilityTimezone || 'UTC', displayCalendar);
  const endLabel = scheduleDateLabel(bookingType?.end || endDate, bookingType, availabilityTimezone || 'UTC', displayCalendar);

  return (
    <Card sx={{ margin: "20px 0", padding: "10px" }}>
      <CardContent>
        <Typography variant="h6" sx={{ marginBottom: 2 }}>
          Booking Overview
        </Typography>
        <BookingShareNotice
          status={getBookingShareStatus(bookingType, { ...(shareAvailabilityOptions || { timezone: availabilityTimezone || 'UTC', hasSchedule: hasAvailabilitySchedule, enabledHours: weeklyAvailability }), displayCalendar })}
          onEdit={target => target === 'hours' ? onEditWeeklyAvailability?.() : onEditDateRange?.()}
        />

        <Grid container spacing={2}>
          {/* Duration */}
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              <Link
                component="button"
                onClick={onEditDuration}
                variant="subtitle1"
                sx={{ textDecoration: "none", cursor: "pointer" }}
              >
                Duration
              </Link>
            </Box>

            <Typography variant="body2">
              {bookingType?.length >= 60
                ? `${Math.floor(bookingType.length / 60)} hour${Math.floor(bookingType.length / 60) > 1 ? "s" : ""
                } ${bookingType.length % 60
                  ? `${bookingType.length % 60} min`
                  : ""
                }`
                : `${bookingType?.length ?? 0} min`}
            </Typography>
          </Grid>

          {/* Date Range */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Link
                component="button"
                onClick={onEditDateRange}
                variant="subtitle1"
                sx={{ textDecoration: "none", cursor: "pointer" }}
              >
                Date Range
              </Link>
            </Box>
            <Typography variant="caption" color="text.secondary">{bookingType?.calendarSystem === 'ethiopic' ? 'Ethiopian calendar' : 'Gregorian calendar'}</Typography>

            <Typography variant="body2">
              <Box sx={{ display: "flex", alignItems: "center", marginBottom: 0, gap: 0 }}>
                <Typography>
                  {bookingType?.recurring !== Recurring.NONE
                    ? `Every ${bookingType?.recurringInterval} ${bookingType?.recurringInterval > 1
                      ? `${bookingType?.recurring}s`
                      : bookingType?.recurring
                    }`
                    : "No recurrence"}
                </Typography>
              </Box>

              <Typography>
                {bookingType?.end ? (
                  <>
                    {startLabel} - {endLabel} ({totalDays} day{totalDays > 1 ? "s" : ""})
                  </>
                ) : (
                  <>Continuous Meeting: {startLabel} (ongoing)</>
                )}
              </Typography>
            </Typography>
          </Grid>

          {/* Weekly Availability */}
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 1,
              }}
            >
              <Link
                component="button"
                onClick={onEditWeeklyAvailability}
                variant="subtitle1"
                sx={{ textDecoration: "none", cursor: "pointer" }}
              >
                Weekly Availability
              </Link>
              <ClickAwayListener onClickAway={() => setHelpOpen(false)}>
                <span>
                  <Tooltip
                    open={helpOpen}
                    onOpen={() => setHelpOpen(true)}
                    onClose={() => setHelpOpen(false)}
                    disableTouchListener
                    describeChild
                    title={<Box>
                      {availabilityHost && <Box sx={{ mb: 0.5 }}>Host: {availabilityHost}{availabilityTimezone ? ` (${availabilityTimezone})` : ""}</Box>}
                      Bookings can start on an available day on or after {startLabel || "your start date"}, including today if a slot is available.
                      {bookingType?.recurring === Recurring.WEEKLY && Number(bookingType?.recurringInterval || 1) === 1
                        ? " Weekly repetition does not delay the first booking until next week."
                        : ""}
                      {" To begin next week, change the start date under Date Range."}
                    </Box>}
                  >
                    <IconButton
                      size="small"
                      aria-label="About weekly availability"
                      onClick={() => setHelpOpen(true)}
                      onKeyDown={event => { if (event.key === "Escape") setHelpOpen(false); }}
                      sx={{ color: "text.secondary", ml: 0.5 }}
                    >
                      <HelpOutlineIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Tooltip>
                </span>
              </ClickAwayListener>
            </Box>

            {!weeklyAvailability?.length && (
              <Typography variant="body2" color="text.secondary">
                {hasAvailabilitySchedule
                  ? "No open weekly hours are selected. Open Weekly Availability to choose days and times."
                  : "Select a host and availability schedule under Weekly Availability to see the weekly hours here."}
              </Typography>
            )}

            {(weeklyAvailability || []).map((hour) => {
              const { parent, children } = hour || {};
              if (!parent) return null;

              return (
                <div key={parent.id}>
                  <Grid container alignItems="center" justifyContent="space-between">
                    <Grid item xs={6}>
                      <Typography variant="body1">{parent.day}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body1" align="right">
                        {parent.onJob ? `${clockLabel(parent.startTime)} - ${clockLabel(parent.endTime)}` : "Closed"}
                      </Typography>
                    </Grid>
                  </Grid>

                  {children && children.length > 0 && (
                    <Grid container sx={{ pl: 2, mt: 0.5 }}>
                      {children.map((child) => (
                        <Grid
                          container
                          key={child.id}
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Grid item xs={6}>
                            <Typography variant="body2">{child.day}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" align="right">
                              {child.onJob ? `${clockLabel(child.startTime)} - ${clockLabel(child.endTime)}` : "Closed"}
                            </Typography>
                          </Grid>
                        </Grid>
                      ))}
                    </Grid>
                  )}

                  <Divider sx={{ my: 1 }} />
                </div>
              );
            })}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default BookingSummary;
